import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';

import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { normalizeAvatarUrl } from 'app/core/user/avatar-url.util';
import { Company } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { NewTrackerService } from 'app/company/tracker/new-tracker.service';
import { Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { FuseConfigService } from '@fuse/services/config';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthSrcDirective } from 'app/modules/shared/directives/auth-src.directive';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    styles: [`
        /* Viewport-locked shell: toolbar stays put while page content scrolls.
           Without this, long mobile pages (e.g. organization profile) grow the
           document and the top navbar scrolls away with the body. */
        :host {
            display: flex;
            flex-direction: row;
            flex: 1 1 auto;
            width: 100%;
            min-width: 0;
            min-height: 0;
            height: 100dvh;
            max-height: 100dvh;
            overflow: hidden;
        }

        :host fuse-vertical-navigation {
            flex: 0 0 var(--fuse-vertical-navigation-width, 280px) !important;
            max-width: var(--fuse-vertical-navigation-width, 280px);
            min-height: 0;
            height: 100%;
        }

        :host .classy-layout__content {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-width: 0;
            min-height: 0;
            width: auto;
            max-width: 100%;
            height: 100%;
            overflow: hidden;
        }

        /* Sticky belt-and-suspenders: even if a child reintroduces document
           scroll, the toolbar pins to the top of its scrollport. */
        :host .classy-layout__toolbar {
            position: sticky;
            top: 0;
            flex: 0 0 auto;
            z-index: 49;
        }

        :host .classy-layout__main {
            position: relative;
            flex: 1 1 auto;
            min-width: 0;
            min-height: 0;
            width: 100%;
            overflow-x: hidden;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
        }
    `],
    imports: [
        AuthSrcDirective,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatInputModule,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        UserComponent,
        NgIf,
        NgTemplateOutlet,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        RouterOutlet,
        QuickChatComponent,
        TranslocoModule,
        MatTooltipModule
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    public branch: FormControl<any> = new FormControl<any>(null);

    isScreenSmall = false;
    navigation: Navigation = {
        compact: [],
        default: [],
        futuristic: [],
        horizontal: [],
    };
    user: Company;
    company: Company;
    companyPhoto: string = '';
    companyName: string = '';
    companyEmail: string = '';
    companyId: string = '';
    isManager: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    appVersion = environment.appVersion;
    copyrightYear = new Date().getFullYear();
    isProduction: boolean;
    /**
     * Dark/light toggle visibility. Its own flag rather than `!isProduction`:
     * that read the build mode as a feature switch, so shipping dark mode to
     * production was impossible without also claiming the build was not production.
     */
    showDarkMode = environment.showDarkMode;
    /**
     * Constructor
     */
    lang: string = 'en';
    selectedBranch: any;
    showArabicLanguage: boolean = environment.showArabicLanguage;
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _translocoService: TranslocoService,
        private _newTracker: NewTrackerService,
        private _fuseConfigService: FuseConfigService
    ) {}

    get currentYear(): number {
        return new Date().getFullYear();
    }

    /* Maps the raw auth role to its nav-bar display label. Product wants
       user-facing role names distinct from the internal role slugs (e.g.
       'company' account -> "Organization Admin", 'staff' -> "Operator"). */
    get roleLabelKey(): string {
        switch (this.userRole) {
            case 'company':
                return 'Organization Admin';
            case 'manager':
                return 'Manager';
            case 'staff':
                return 'Operator';
            case 'admin':
                return 'Super Admin';
            default:
                return this.userRole;
        }
    }

    menuList: any[] = [];
    userRole: string = '';
    ngOnInit(): void {
        this.isProduction = environment.production;
        this.userRole = localStorage.getItem('userRole');
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((lang) => {
                this.lang = lang;
                this._changeDetectorRef.detectChanges();
            });

        if (this.userRole === 'company') {
            this.companyName = localStorage.getItem('user_name');
            this.companyPhoto = normalizeAvatarUrl(localStorage.getItem('user_image'));
            this.companyEmail = localStorage.getItem('email');
            this._userService
                .getcompany()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((res) => {
                    if (!this.companyName) {
                        this.companyName = res.company_name;
                        if (!localStorage.getItem('user_name')) {
                            localStorage.setItem('user_name', this.companyName);
                        }
                    }
                    this._userService.companyName
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe((res) => {
                            this.companyName = res;
                            localStorage.setItem('user_name', this.companyName);
                        });
                    this._userService.companyPhoto
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe((res) => {
                            this.companyPhoto = normalizeAvatarUrl(res);
                            if (this.companyPhoto) {
                                localStorage.setItem(
                                    'user_image',
                                    this.companyPhoto
                                );
                            } else {
                                localStorage.removeItem('user_image');
                            }
                        });
                    if (!this.companyPhoto) {
                        this.companyPhoto = normalizeAvatarUrl(res.image);

                        if (this.companyPhoto && !localStorage.getItem('user_image')) {
                            localStorage.setItem(
                                'user_image',
                                this.companyPhoto
                            );
                        }
                    }
                    if (!this.companyEmail) {
                        this.companyEmail = res.email;
                    }
                    this._changeDetectorRef.markForCheck();
                });
            this._changeDetectorRef.markForCheck();
            this._userService.companyId
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((res) => {
                    this.companyId = res;
                    this._changeDetectorRef.markForCheck();
                });
        } else if (this.userRole === 'chatting') {
            this.companyName = localStorage.getItem('email');
            this.companyEmail = localStorage.getItem('email');
        } else if (this.userRole === 'manager' || this.userRole === 'staff') {
            this.companyName = localStorage.getItem('user_name');
            this.companyPhoto = normalizeAvatarUrl(localStorage.getItem('user_image'));
            this.companyEmail = localStorage.getItem('email');
            this._userService
                .loadMemberStaffProfile()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((profile) => {
                    if (profile.name) {
                        this.companyName = profile.name;
                    }
                    if (profile.email) {
                        this.companyEmail = profile.email;
                    }
                    this._changeDetectorRef.markForCheck();
                });
            this._userService.memberStaffPhoto$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((photo) => {
                    this.companyPhoto = normalizeAvatarUrl(photo);
                    this._changeDetectorRef.markForCheck();
                });
        }
        this._changeDetectorRef.markForCheck();
        this._navigationService.navigation$
            .pipe(take(1))
            .subscribe((navigation: Navigation) => {
                this.navigation = { ...navigation };
                this._changeDetectorRef.markForCheck();
            });
        this._changeDetectorRef.markForCheck();
        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }
    isDark =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('theme') === 'dark';
    toggleTheme(): void {
        this.isDark = !this.isDark;
        const themeClass = this.isDark ? 'theme-default-dark' : 'theme-default';

        this._fuseConfigService.config = {
            scheme: this.isDark ? 'dark' : 'light',
            theme: themeClass
        };

        localStorage.setItem('theme', this.isDark ? 'dark' : 'light');
    }
  
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /** Roles that have a profile page the nav header can open. Admin has none. */
    get canOpenProfile(): boolean {
        return (
            this.userRole === 'company' ||
            this.userRole === 'manager' ||
            this.userRole === 'staff'
        );
    }

    /* A pointer press leaves the nav-header button focused, so the brand focus
       ring stays painted on it after the click. Keyboard activation reports
       detail === 0, so dropping focus only for real pointer presses keeps the
       ring where it belongs: on Tab/Enter/Space. */
    private _blurAfterPointer(event?: MouseEvent): void {
        if (event && event.detail > 0) {
            (event.currentTarget as HTMLElement | null)?.blur();
        }
    }

    goToProfile(event?: MouseEvent) {
        this._blurAfterPointer(event);
        if (this.userRole === 'company') {
            this._router.navigate(['/pages/profile']);
        } else if (this.userRole === 'manager' || this.userRole === 'staff') {
            this._router.navigate(['/pages/my-profile']);
        }
    }

    navigateToDashboard(event?: MouseEvent) {
        this._blurAfterPointer(event);
        this._router.navigate(['/overview']);
    }

    goToSupplierProfile() {
        this._router.navigate(['/supplier-profile']);
    }

    toggleNavigation(name: string): void {
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );
        if (navigation) {
            navigation.toggle();
            this._newTracker.emitToggle(); // ✅ Emit event
        }
    }
}
